import { getApiResponseEndpoint, getApiResponseStatus, isExpectedApiResponseError } from "./Auth";

const APP_FRAME_PATTERN = /client\/src|\.\/src\//i;
const TIPTAP_IFRAME_BOUNDARY_PATTERN = /TiptapDocumentSB\/index\.js/i;

function getExceptionValues(event) {
  return event?.exception?.values || [];
}

function getExceptionText(event) {
  return getExceptionValues(event)
    .map((exception) => `${exception.type || ""}: ${exception.value || ""}`.trim())
    .join("\n");
}

function getFrames(event) {
  return getExceptionValues(event).flatMap((exception) => exception.stacktrace?.frames || []);
}

function getFrameFilenames(event) {
  return getFrames(event).map((frame) => frame.filename || frame.abs_path || "");
}

function hasFrameMatching(event, matcher) {
  return getFrameFilenames(event).some((filename) => matcher.test(filename));
}

function getSourceMappedAppFrames(event) {
  return getFrameFilenames(event).filter((filename) => APP_FRAME_PATTERN.test(filename));
}

function hasSourceMappedAppFrame(event) {
  return getSourceMappedAppFrames(event).length > 0;
}

function getBreadcrumbText(event) {
  return (event?.breadcrumbs || [])
    .map((breadcrumb) => {
      const data = breadcrumb.data || {};
      return [breadcrumb.message, data.url, data.status_code, data.method].filter(Boolean).join(" ");
    })
    .join("\n");
}

function getEventUrlText(event) {
  return [event?.request?.url, event?.tags?.url, event?.tags?.endpointUrl, event?.transaction].filter(Boolean).join("\n");
}

// Environment-only filters keep local QA and PR preview noise out of production
// user-pain triage. Do not broaden this to staging/editor hosts unless we decide
// those surfaces should never count as production frontend crashes.
function isPreviewHostEvent(event) {
  return /https?:\/\/sudowrite-pr-\d+\.onrender\.com\b/i.test(getEventUrlText(event));
}

function isLocalDevelopmentEvent(event) {
  const text = getEventUrlText(event);
  return event?.tags?.environment === "development" && /https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\b/i.test(text);
}

// Third-party service filters are for vendor-owned failures, not Sudowrite app
// crashes that happen near a vendor breadcrumb. Prefer vendor frames or vendor
// request URLs as the signal when adding to this group.
function isExternalAnalyticsFailure(event) {
  if (hasSourceMappedAppFrame(event)) {
    return false;
  }

  const text = `${getExceptionText(event)}\n${getBreadcrumbText(event)}\n${getEventUrlText(event)}`;
  return /HTTP Client Error with status code: 500/i.test(text) && (/cdp\.customer\.io\/v1\/projects\/[^/]+\/settings/i.test(text) || hasFrameMatching(event, /\/analytics\.min\.js/i));
}

// Firebase auth infrastructure outages are expected to recover through retry or
// reauth. Keep this scoped to transient network/503/unavailable states so real
// client/auth contract failures stay reportable.
function isFirebaseAuthTransient(event) {
  const text = `${getExceptionText(event)}\n${getBreadcrumbText(event)}\n${getEventUrlText(event)}`;
  if (!/securetoken\.googleapis\.com\/v1\/token|@firebase\/auth/i.test(text) && !hasFrameMatching(event, /@firebase\/auth/i)) {
    return false;
  }

  return /Network Error|HTTP Client Error with status code: 503|UNAVAILABLE|service is currently unavailable/i.test(text);
}

// Hocuspocus token refresh failures are follow-on session/reconnect churn. The
// strategy is to suppress expired-credential noise from the collaborative editor
// loop while preserving unrelated app errors that merely have auth breadcrumbs.
function isHocuspocusTokenRefreshNoise(event) {
  const text = `${getExceptionText(event)}\n${getBreadcrumbText(event)}`;
  if (/CustomEvent.*unhandledrejection/i.test(text) && /\[hocuspocus\]|securetoken\.googleapis\.com\/v1\/token/i.test(text)) {
    return true;
  }

  return /credential is no longer valid/i.test(text) && /\[hocuspocus\]|securetoken\.googleapis\.com\/v1\/token.*400/i.test(text);
}

// Browser/runtime vendor filters are only for events with no actionable
// Sudowrite frame. If a source-mapped client frame appears, keep the event so we
// can catch integration bugs instead of hiding them as runtime noise.
function isThirdPartyBrowserRuntimeNoise(event) {
  if (hasSourceMappedAppFrame(event)) {
    return false;
  }

  const text = getExceptionText(event);
  if (hasFrameMatching(event, /@castleio\/castle-js|\/castle\.js/i)) {
    return true;
  }

  const filenames = getFrameFilenames(event).filter(Boolean);
  if (/^Error: illegal access$/i.test(text) && filenames.length === 0) {
    return true;
  }

  if (/Maximum call stack size exceeded/i.test(text) && filenames.length > 0 && filenames.every((filename) => filename === "undefined")) {
    return true;
  }

  if (/Object captured as exception with keys: a, code, message/i.test(text) && filenames.length === 0) {
    return true;
  }

  return false;
}

// Cross-origin iframe access is expected around the editor boundary. Keep this
// focused on known iframe setup/inspection paths so genuine app-side frame access
// bugs remain visible.
function isCrossOriginIframeAccessNoise(event) {
  const text = getExceptionText(event);
  if (!/SecurityError.*Blocked a frame with origin|Failed to read a named property 'document' from 'Window'/i.test(text)) {
    return false;
  }

  const appFrames = getSourceMappedAppFrames(event);
  if (appFrames.length === 0) {
    return true;
  }

  return appFrames.length === 1 && TIPTAP_IFRAME_BOUNDARY_PATTERN.test(appFrames[0]);
}

// Static-chunk filters are source-map confidence filters: drop opaque minified
// signatures only when there is no source-mapped app frame to debug.
function isUnmappedStaticChunkNoise(event) {
  const text = getExceptionText(event);
  const filenames = getFrameFilenames(event).filter(Boolean);
  if (hasSourceMappedAppFrame(event)) {
    return false;
  }

  const onlyStaticChunks = filenames.length > 0 && filenames.every((filename) => filename === "<anonymous>" || /\/static\/js\/.+\.js$/i.test(filename));
  if (!onlyStaticChunks) {
    return false;
  }

  return /can't access property "parse", .* is undefined|Object captured as exception with keys: a, code, message/i.test(text);
}

// API follow-on filters remove downstream library explosions after a backend/API
// failure has already been represented elsewhere. The app-frame guard is the
// important safety check: if client code is in the stack, keep the event.
function isExpectedApiFollowOnNoise(event) {
  const text = `${getExceptionText(event)}\n${getBreadcrumbText(event)}`;
  if (hasSourceMappedAppFrame(event)) {
    return false;
  }

  return (
    /Cannot read properties of undefined \(reading 'apply'\)|undefined is not an object \(evaluating '.*\.apply'\)/i.test(text) &&
    (/Error: Server problem|HTTP Client Error with status code: 500|\/api\/annotations\/new/i.test(text) || /\[hocuspocus\]|\/app\/documents\/[^/]+\/feedback/i.test(text))
  );
}

// Yjs/Tiptap sync filters target recoverable collaboration-layer churn. These
// should stay tied to library sync frames and generic sync TypeErrors, not broad
// app exceptions that happen to involve the editor.
function isRecoverableYjsSyncNoise(event) {
  const text = getExceptionText(event);
  if (hasSourceMappedAppFrame(event)) {
    return false;
  }

  if (!/Cannot convert undefined or null to object|Cannot read properties of undefined \(reading 'apply'\)|undefined is not an object \(evaluating '.*\.apply'\)/i.test(text)) {
    return false;
  }

  return hasFrameMatching(event, /y-prosemirror\/src\/plugins\/sync-plugin|y-indexeddb\/src\/y-indexeddb|@tiptap\/core\/dist/i);
}

// Keep the drop list ordered from highest-confidence environment/vendor filters
// toward broader source-map and collaboration heuristics. New filters should make
// their ownership signal explicit so first-party crashes are preserved.
function shouldDropKnownLowConfidenceNoise(event) {
  return (
    isLocalDevelopmentEvent(event) ||
    isPreviewHostEvent(event) ||
    isExternalAnalyticsFailure(event) ||
    isFirebaseAuthTransient(event) ||
    isHocuspocusTokenRefreshNoise(event) ||
    isThirdPartyBrowserRuntimeNoise(event) ||
    isCrossOriginIframeAccessNoise(event) ||
    isUnmappedStaticChunkNoise(event) ||
    isExpectedApiFollowOnNoise(event) ||
    isRecoverableYjsSyncNoise(event)
  );
}

function tagApiResponseError(event, error) {
  const status = getApiResponseStatus(error);
  if (status === null) {
    return event;
  }

  const endpointUrl = getApiResponseEndpoint(error);
  event.tags = {
    ...event.tags,
    statusCode: String(status),
    ...(endpointUrl ? { endpointUrl } : {}),
  };
  return event;
}

export function filterFrontendSentryEvent(event, hint) {
  const error = hint?.originalException;
  if (isExpectedApiResponseError(error)) {
    return null;
  }

  if (shouldDropKnownLowConfidenceNoise(event)) {
    return null;
  }

  // Callers often capture or accidentally leak Auth.fetchWithIdToken errors
  // after Auth rethrows them. Keep reportable 4xx events, but tag them the same
  // way Auth does so future triage can sort by status and endpoint reliably.
  return tagApiResponseError(event, error);
}
